import {AstView, EnrichedAstNode, RenderVariantsType} from 'rendition-core/types';
import _ from 'lodash';
import {extractUnitAndValue} from 'rendition-core/UnitConversion/utils/extractUnitAndValue';
import {CSSProperties} from 'react';

export type RenderDataNode = {
    nodeId: string;
    styles: EnrichedAstNode['styles'];
    children: RenderDataNode[];
    tag: 'img' | 'span' | 'div';
    imgUrl?: string;
    text?: string | undefined;
    disabledCallbacks?: boolean;
};

export const generateRenderData = (root: EnrichedAstNode, view: AstView) => {
    // TODO
    // traverse Tree.
    // Get Properties, and id into nice structure.
    return translateNode(root, view, {isRoot: true});
};

export const generateChangedList = (lastRenderRoot: RenderDataNode, newRenderRoot: RenderDataNode) => {
    // TODO
    // ID is added if children list doesn't match
    // Id is added if styles don't match last render.

    const oldRenderList = flattenRenderDataNodeTree(lastRenderRoot);
    const newRenderList = flattenRenderDataNodeTree(newRenderRoot);

    const reRenderList: {[key: string]: boolean} = {};

    newRenderList.forEach((newRenderNode) => {
        const lastRenderNode = findNodeByIdInList(newRenderNode.nodeId, oldRenderList);

        if (!lastRenderNode || shouldReRender(lastRenderNode, newRenderNode)) {
            reRenderList[newRenderNode.nodeId] = true;
            const parents = findParents(newRenderRoot, newRenderNode);
            parents.forEach((parent) => {
                reRenderList[parent.nodeId] = true;
            });
        }

        // compare node to last.
        // if different add it and parents to rerenders.
    });

    return Object.keys(reRenderList);
};

const shouldReRender = (lastRender: RenderDataNode, newRender: RenderDataNode) => {
    return _.isEqual(lastRender.styles, lastRender.styles);
};

const findParents: (root: RenderDataNode, node: RenderDataNode) => RenderDataNode[] = (
    root: RenderDataNode,
    node: RenderDataNode
) => {
    let working: RenderDataNode | undefined = node;
    const newRenderList = flattenRenderDataNodeTree(root);
    const results: RenderDataNode[] = [];

    while (working) {
        const parent = newRenderList.find((node) => node.children.includes(node));
        if (parent) {
            results.push(parent);
        }
        working = parent;
    }

    return results;
};

export const flattenRenderDataNodeTree = (root: RenderDataNode) => {
    const nodeList: RenderDataNode[] = [root];

    root.children.forEach((child) => {
        nodeList.push(...flattenRenderDataNodeTree(child));
    });

    return nodeList;
};

function findNodeByIdInList(id: string, list: RenderDataNode[]) {
    const result = list.find((node) => node.nodeId === id);

    return result;
}

const translateNode: (
    node: EnrichedAstNode,
    view: AstView,
    options: {isRoot?: boolean; textChild?: boolean; forceFixedSizing?: boolean; isUserSelectable?: boolean}
) => RenderDataNode = (node, view, options) => {
    const nodeToBuild = getNodeToBuild(node, view.meta.complexComponentMap);

    const styles = getStyles(
        nodeToBuild,
        false,
        view.tree.position.absoluteX,
        view.tree.position.absoluteY,
        [],
        [],
        [],
        !!options.forceFixedSizing,
        !!options.isRoot,
        !!options.isUserSelectable
    );
    const textChild =
        nodeToBuild.type === 'Text' &&
        nodeToBuild.properties['childText']?.type === 'string' &&
        nodeToBuild.properties['childText'].value;

    const renderDataNode: RenderDataNode = {
        nodeId: nodeToBuild.internalId,
        styles,
        children: node.children.map((child) =>
            translateNode(child, view, {
                textChild: nodeToBuild.type === 'Text',
                isUserSelectable: styles.isUserSelectable,
            })
        ),
        tag: nodeToBuild.type === 'Image' ? 'img' : options.textChild ? 'span' : 'div',
        text: textChild || undefined,
        disabledCallbacks: !!options.textChild,
    };

    return renderDataNode;
};

const getNodeToBuild = (node: EnrichedAstNode, complexComponentMap: AstView['meta']['complexComponentMap']) => {
    if (node.type !== 'Instance') {
        return node;
    }
    const component = (complexComponentMap[node.refId].renderVariants as RenderVariantsType).finalTree;
    const instanceCss: {[key: string]: string} = {};

    node.mandatoryProperties.forEach((key) => {
        instanceCss[key] = node.styles[key];
    });
    return {
        ...component,
        children: node.children,
        properties: node.properties,
        internalId: node.internalId,
        position: node.position,
        innerPosition: node.innerPosition,
        styles: {...component.styles, ...instanceCss},
    };
};

const IGNORE_LIST_CSS = ['pointerEvents', 'cursor'];

export const getStyles: (
    node: EnrichedAstNode,
    fixedPosition: boolean | undefined,
    offSetX: number,
    offSetY: number,
    hiddenNodes: string[],
    translucentNodes: string[],
    unclickableNodes: string[],
    forceFixedSizing: boolean,
    isRoot: boolean,
    isUserSelectable: boolean
) => {[key: string]: any} = (
    node,
    fixedPosition,
    offSetX,
    offSetY,
    hiddenNodes,
    translucentNodes,
    unclickableNodes,
    forceFixedSizing,
    isRoot,
    isUserSelectable
) => {
    const propertyStyles: CSSProperties = {};
    Object.keys(node.properties).forEach((key) => {
        if (node.properties[key].subType === 'style') {
            propertyStyles[key] = node.properties[key].value;
        }
    });

    const fixedPositionCss: CSSProperties = fixedPosition
        ? {
              position: 'absolute',
              width: node.position.width,
              height: node.position.height,
              top: node.position.absoluteY - offSetY,
              left: node.position.absoluteX - offSetX,
              marginLeft: 0,
              marginRight: 0,
              marginTop: 0,
              marginBottom: 0,
              margin: 0,
          }
        : {};

    const editorOverrides: CSSProperties = {};
    if (isUserSelectable !== true) {
        editorOverrides.userSelect = 'none';
    }

    Object.keys(node.styles).forEach((cssKey) => {
        if (typeof node.styles[cssKey] === 'string' && node.styles[cssKey].includes('vw')) {
            const {value} = extractUnitAndValue(node.styles[cssKey]);
            editorOverrides[cssKey] = `calc(var(--preview-width) * (${value} / 100))`;
        }
    });

    const finalCSS: CSSProperties = {
        ...node.styles,
        ...propertyStyles,
        ...fixedPositionCss,
        ...editorOverrides,
    };

    IGNORE_LIST_CSS.forEach((key) => {
        delete finalCSS[key];
    });

    if (hiddenNodes.includes(node.internalId)) {
        finalCSS.visibility = 'hidden';
    }

    if (translucentNodes.includes(node.internalId)) {
        finalCSS.opacity = '0.1';
    }

    if (unclickableNodes.includes(node.internalId)) {
        finalCSS.pointerEvents = 'none';
    }

    if (isRoot) {
        delete finalCSS.marginLeft;
        delete finalCSS.marginRight;
        delete finalCSS.marginTop;
        delete finalCSS.marginBottom;
        delete finalCSS.position;

        if (
            node.calculatedLayoutAbstraction!.units.width === '%' ||
            node.calculatedLayoutAbstraction!.units.width === 'vw'
        ) {
            finalCSS.width = '100%';
        }
        if (
            node.calculatedLayoutAbstraction!.units.height === '%' ||
            node.calculatedLayoutAbstraction!.units.height === 'vw'
        ) {
            finalCSS.height = node.innerPosition.height + 'px';
        }
    }

    if (!finalCSS.boxSizing) {
        finalCSS.boxSizing = 'content-box';
    }

    if (forceFixedSizing) {
        finalCSS.height = node.position.height + 'px';
        finalCSS.width = node.position.width + 'px';
    }

    return finalCSS;
};
